import React from "react";
import { Alert } from "react-bootstrap";
import styled from "styled-components";

import { WidgetTheme } from "../../types/analytics";
import { formatHumanDuration } from "../../utils/analytics";
import WidgetInfoButton from "./WidgetInfoButton";

export type MetricCardValueFormat = "number" | "percent" | "decimal" | "durationHours" | "durationSeconds";

export type MetricCardItem = {
    key: string;
    label: string;
    value: number | null;
    changePercent?: number | null;
    numericChange?: number | null;
    valueFormat?: MetricCardValueFormat;
    description?: string;
    invertTrend?: boolean;
};

type MetricCardsProps = {
    metrics: MetricCardItem[];
    theme: WidgetTheme;
    emptyMessage?: string;
    combineChangeAndDelta?: boolean;
    noChangeLabel?: string;
    minCardWidth?: string;
    maxCardWidth?: string;
    gridGap?: string;
    cardMinHeight?: string;
};

const MetricCardsGrid = styled.div<{ $minCardWidth: string; $maxCardWidth: string; $gridGap: string }>`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, ${props => props.$minCardWidth}), ${props => props.$maxCardWidth}));
    gap: ${props => props.$gridGap};
    align-items: stretch;
    justify-content: start;
`;

const MetricCard = styled.div<{ theme: WidgetTheme }>`
    position: relative;
    border: 1px solid ${props => props.theme.border};
    border-radius: 8px;
    padding: 16px;
    background: ${props => props.theme.chartBg};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
`;

const MetricLabel = styled.div<{ theme: WidgetTheme }>`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.9rem;
    color: ${props => props.theme.text};
    opacity: 0.7;
    margin-bottom: 8px;
`;

const MetricValue = styled.div<{ theme: WidgetTheme }>`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
    color: ${props => props.theme.text};
`;

const MetricChange = styled.div<{ $positive: boolean; $negative: boolean; $positiveColor: string; $negativeColor: string }>`
    margin-top: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${props => props.$positive ? props.$positiveColor : props.$negative ? props.$negativeColor : "#6c757d"};
`;

const formatNumber = (value: number | null, format: MetricCardValueFormat): string => {
    if (value === null || value === undefined) {
        return "-";
    }

    if (format === "percent") {
        return `${value.toFixed(2)}%`;
    }

    if (format === "decimal") {
        return value.toFixed(2);
    }

    if (format === "durationHours") {
        return formatHumanDuration(value, "hours");
    }

    if (format === "durationSeconds") {
        return formatHumanDuration(value, "seconds");
    }

    return value.toLocaleString();
};

const formatSigned = (value: number | null, suffix = ""): string => {
    if (value === null || value === undefined) {
        return "-";
    }

    const sign = value > 0 ? "+" : "";
    const isWholeNumber = Number.isInteger(value);
    const formattedValue = isWholeNumber ? value.toLocaleString() : value.toFixed(2);

    return `${sign}${formattedValue}${suffix}`;
};

const MetricCards: React.FC<MetricCardsProps> = ({
    metrics,
    theme,
    emptyMessage = "No KPI data available for the selected date range.",
    combineChangeAndDelta = false,
    noChangeLabel = "—",
    minCardWidth = "180px",
    maxCardWidth = "200px",
    gridGap = "16px",
    cardMinHeight = "132px"
}) => {
    if (metrics.length === 0) {
        return <Alert variant="info">{emptyMessage}</Alert>;
    }

    return (
        <MetricCardsGrid $minCardWidth={minCardWidth} $maxCardWidth={maxCardWidth} $gridGap={gridGap}>
            {metrics.map(metric => {
                const changePercent = metric.changePercent ?? null;
                const numericChange = metric.numericChange ?? null;
                const trendValue = changePercent ?? numericChange ?? 0;
                const positive = metric.invertTrend ? trendValue < 0 : trendValue > 0;
                const negative = metric.invertTrend ? trendValue > 0 : trendValue < 0;

                let changeText = noChangeLabel;
                if (combineChangeAndDelta && changePercent !== null && numericChange !== null) {
                    changeText = `${formatSigned(changePercent, "%")} (${formatSigned(numericChange)})`;
                } else if (changePercent !== null) {
                    changeText = formatSigned(changePercent, "%");
                } else if (combineChangeAndDelta && numericChange !== null) {
                    changeText = formatSigned(numericChange);
                }

                return (
                    <MetricCard key={metric.key} theme={theme} className="w-100 h-100" style={{ minHeight: cardMinHeight }}>
                        {metric.description && (
                            <div style={{ position: "absolute", top: "8px", right: "8px", zIndex: 1 }}>
                                <WidgetInfoButton description={metric.description} placement="auto" />
                            </div>
                        )}
                        <MetricLabel theme={theme}>
                            <span>{metric.label}</span>
                        </MetricLabel>
                        <MetricValue theme={theme}>{formatNumber(metric.value, metric.valueFormat || "number")}</MetricValue>
                        <MetricChange $positive={positive} $negative={negative} $positiveColor={theme.positiveColor} $negativeColor={theme.negativeColor}>
                            {changeText}
                        </MetricChange>
                    </MetricCard>
                );
            })}
        </MetricCardsGrid>
    );
};

export default MetricCards;
